import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { Building2, Check, Star, ArrowRight, Gift, Sparkles } from "lucide-react";

const PLANS = [
  { key: "basic", name: "Basic", price: 1499, total: 1769, monthly: 125,
    features: ["Sales & purchase billing", "Customer / supplier records", "Payment & receipt entries", "Expense management", "Basic inventory", "GST invoices", "Basic reports", "Data backup"] },
  { key: "professional", name: "Professional", price: 2499, total: 2949, monthly: 208, featured: true,
    features: ["Everything in Basic", "Complete accounting", "Advanced inventory", "GSTR-1 & GSTR-3B", "Outstanding receivable / payable", "P&L + Balance Sheet", "Multiple invoice formats", "Barcode support", "WhatsApp invoice sharing", "Advanced reports"] },
  { key: "business", name: "Business", price: 3999, total: 4719, monthly: 333,
    features: ["Everything in Professional", "Multiple users", "Multiple branches / godowns", "User permissions", "Stock transfer", "Advanced dashboard", "Priority support", "Enhanced security"] },
];

const fmt = (n) => `₹${n.toLocaleString("en-IN")}`;

export default function FirmSignup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = plan pick, 2 = details form
  const [plan, setPlan] = useState("professional");
  const [f, setF] = useState({
    firm_name: "", login_id: "", password: "", edit_pin: "",
    owner_username: "", owner_password: "",
    accountant_username: "", accountant_password: "",
    contact: "", email: "", address: "", state: "", gstin: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!f.firm_name || !f.login_id || !f.password || !f.edit_pin) {
      toast.error("Please fill firm name, login id, password, and edit PIN"); return;
    }
    if (!f.owner_username || !f.owner_password || !f.accountant_username || !f.accountant_password) {
      toast.error("Set owner & accountant credentials"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/firms/signup", { ...f, plan });
      const trialEnd = data.trial_end_at ? new Date(data.trial_end_at).toLocaleDateString() : null;
      toast.success(`Registration submitted! 2 months FREE trial active till ${trialEnd || "60 days from now"}. Awaiting Super Admin approval.`);
      // Notify helpline via WhatsApp draft
      const msg = `Hello ANV Associate, I just registered *${f.firm_name}* on AccountifyPro with the *${plan.toUpperCase()}* plan. Please approve. Contact: ${f.contact || "—"} · GSTIN: ${f.gstin || "—"}`;
      window.open(`https://wa.me/918446463026?text=${encodeURIComponent(msg)}`, "_blank");
      navigate("/firm/login");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  const F = (k, l, extra = {}) => (
    <div>
      <Label htmlFor={k}>{l}</Label>
      <Input id={k} data-testid={`signup-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...extra} />
    </div>
  );

  if (step === 1) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <div className="overline mb-2 flex items-center justify-center gap-2">
              <Sparkles size={12} className="text-[#064E3B]" /> Register your firm
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Pick a plan to get started</h1>
            <div className="mt-4 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold" data-testid="signup-trial-banner">
              <Gift size={14} /> First <b>2 months</b> FREE on every plan — no charges at enrolment.
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {PLANS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPlan(p.key)}
                data-testid={`signup-plan-${p.key}`}
                className={`relative text-left rounded-xl border p-5 bg-white transition-all ${
                  plan === p.key
                    ? "border-[#064E3B] border-2 shadow-xl md:-translate-y-1"
                    : "border-border hover:border-slate-400"
                }`}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-[#064E3B] text-white text-[10px] font-bold px-2 py-1 rounded-full">
                    <Star size={10} fill="currentColor" /> RECOMMENDED
                  </div>
                )}
                <div className="overline">{p.name}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-black tracking-tight">{fmt(p.price)}</span>
                  <span className="text-slate-500 text-xs">+ GST / yr</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 font-mono">Total {fmt(p.total)} · ≈ {fmt(p.monthly)}/mo</div>
                <ul className="mt-4 space-y-1">
                  {p.features.slice(0, 6).map((ft) => (
                    <li key={ft} className="flex items-start gap-2 text-xs text-slate-700">
                      <Check size={12} className="text-[#166534] mt-0.5 flex-shrink-0" />
                      <span>{ft}</span>
                    </li>
                  ))}
                  {p.features.length > 6 && <li className="text-[10px] text-slate-500 pl-4">+ {p.features.length - 6} more…</li>}
                </ul>
                <div className={`mt-4 text-center text-xs font-semibold ${plan === p.key ? "text-[#064E3B]" : "text-slate-400"}`}>
                  {plan === p.key ? "✓ Selected" : "Click to select"}
                </div>
              </button>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/firm/login" className="text-sm text-[#064E3B] underline">Already registered? Login</Link>
            <Button className="btn-primary px-8" onClick={() => setStep(2)} data-testid="signup-plan-continue">
              Continue with {PLANS.find((p) => p.key === plan)?.name} <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const selectedPlan = PLANS.find((p) => p.key === plan);
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6">
      <div className="w-full max-w-3xl bg-white border border-border rounded-lg p-8">
        <div className="flex items-center gap-2 mb-1"><Building2 size={16} className="text-[#064E3B]" /><span className="overline">Register your firm</span></div>
        <h1 className="text-3xl font-bold tracking-tight">Create AccountifyPro account</h1>
        <p className="text-sm text-muted-foreground mt-1">Submitted registrations are reviewed by ANV Associate super admin.</p>

        <div className="mt-4 flex items-center justify-between border border-[#A7F3D0] bg-[#ECFDF5] rounded-md p-3" data-testid="signup-selected-plan">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-bold">Selected plan</div>
            <div className="font-semibold text-slate-900">{selectedPlan.name} · {fmt(selectedPlan.price)} + GST / year</div>
            <div className="text-xs text-emerald-700 flex items-center gap-1 mt-0.5"><Gift size={11} /> 2 months FREE trial included — first charge after 60 days.</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setStep(1)} data-testid="signup-change-plan">Change plan</Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          {F("firm_name", "Firm Name*")}
          {F("login_id", "Login ID*")}
          {F("password", "Password*", { type: "password" })}
          {F("edit_pin", "Edit PIN* (used for every edit / entry)", { maxLength: 6 })}
          <div className="sm:col-span-2 h-px bg-border" />
          {F("owner_username", "Owner Username*")}
          {F("owner_password", "Owner Password*", { type: "password" })}
          {F("accountant_username", "Accountant Username*")}
          {F("accountant_password", "Accountant Password*", { type: "password" })}
          <div className="sm:col-span-2 h-px bg-border" />
          {F("contact", "Contact")}
          {F("email", "Email")}
          {F("state", "State (for GST intra/inter split)")}
          {F("gstin", "GSTIN")}
          <div className="sm:col-span-2">{F("address", "Address")}</div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <Link to="/firm/login" className="text-sm text-[#064E3B] underline">Already registered? Login</Link>
          <Button className="btn-primary" disabled={busy} onClick={submit} data-testid="signup-submit">
            {busy ? "Submitting..." : "Submit registration & start free trial"}
          </Button>
        </div>
      </div>
    </div>
  );
}
